import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Tooltip } from "@material-ui/core";
import { Spin as Hamburger } from "hamburger-react";
import {
    ArrowDropDown as ArrowDropDownIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
    ViewModule as ViewModuleIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    ChromeReaderMode as ChromeReaderModeIcon,
    ModeCommentOutlined as ModeCommentIcon,
    PhoneAndroid,
    Person as PersonIcon,
} from "@material-ui/icons";

import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap10, Gap20 } from "js/react/components/Gap";

import PresentationActionsMenu from "./PresentationActionsMenu";
import { usePrev } from "js/react/hooks/usePrev";

import { isFullsceenAllowed, isFullscreen, toggleFullscreen } from "../helpers/fullscreen";
import { stopPropagation } from "../helpers/stopPropagation";

export const expandDuration = 600;

const BottomBarAnchor = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 10;
`;

const BottomBar = styled.div.attrs(({
    expanded,
    actionsMenuIsOpen,
}) => ({
    style: {
        maxWidth: expanded || actionsMenuIsOpen ? "1200px" : "44px",
        // Transform differently depending on the expandded state
        transformOrigin: expanded ? "bottom right" : "center",
    }
}))`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  height: 44px;
  background: rgba(0, 0, 0, 0.7);
  padding: 0;
  border-radius: 100px;
  transform: scale(${props => props.visible ? 1 : 0});
  transition:
    transform cubic-bezier(0.0, 0.5, 0.85, 1.75) 0.4s,
    max-width ease-out ${expandDuration}ms
  ;

  @media (max-width: 800px) {
    .hide-800 {
        display: none;
    }
  }
`;

const Divider = styled.div`
    width: 1px;
    min-width: 1px;
    height: 24px;
    background: #ccc;
`;

const Spacer = styled.div`
    width: 80px;
    min-width: 80px;
    height: 20px;

    @media (max-width: 1100px) {
        width: 20px;
        min-width: 20px;
    }
`;

const ShrinkZone = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  overflow: hidden;
  white-space: nowrap;
`;

const HamburgerContainer = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  margin: -2px;
`;

const PresentationName = styled.div`
  color: #ccc;
  font-size: 14px;
  white-space: nowrap;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const CreatorContainer = styled.div`
  display: flex;
  align-items: center;
  height: 30px;

  > img {
    border-radius: 50%;
    height: 24px;
    width: 24px;
    float: left;
    margin-right: 10px;
  }

  > svg {
    color: #ccc;
    font-size: 30px;
    margin-right: 10px;
  }

  > span {
    color: #ccc;
    font-size: 14px;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
`;

const SlideIndex = styled.div`
  user-select: none;
  text-transform: uppercase;
  font-size: 13px;
  color: #fff;
  margin-left: 8px;
  margin-right: 8px;
  white-space: nowrap;
`;

const ButtonWithLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e1e1e1;
  cursor: pointer;

  label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
  }

  &:hover {
    color: #11a9e2;
  }
`;

function getStyledIconButton(Icon, style = {}) {
    return styled(Icon).attrs(() => ({ style }))`
      color: #ccc;
      cursor: pointer;

      &:hover {
        color: #11a9e2;
      }
    `;
}

const PrevSlideButton = getStyledIconButton(NavigateBeforeIcon);
const NextSlideButton = getStyledIconButton(NavigateNextIcon);
const ThumbnailsButton = getStyledIconButton(ViewModuleIcon);

function PlayerControls(props) {
    const {
        visible,
        expanded,
        toggleExpanded,
        registerUserInteraction,
        showThumbnailsPane,
        advanceToSlide,
        currentSlideIndex,
        isPresenterAvailable,
        togglePresenter,
        hasViewerComments,
        toggleComments,
        toggleNotes,
        isPlayerSettingsPreview,
        actionsMenuIsOpen,
        onToggleActionsMenu,
        onControlRemotely,
        slidesCount = 1,
        isRemoteControlLeader,
        canControlRemotely,
        showTitle,
        showAuthor,
        presentationName,
        creator,
        trackSlideNavigation
    } = props;

    const moreButtonRef = useRef();

    const allowFullscreenOption = isFullsceenAllowed();
    const inFullscreen = isFullscreen();

    // Close tooltips if the controls are collapsed or hidden
    const [activeTooltip, setActiveTooltip] = useState(null);
    const handleTooltipClose = name => {
        if (name === activeTooltip) {
            setActiveTooltip(null);
        }
    };
    const prevVisible = usePrev(visible);
    const prevExpanded = usePrev(expanded);
    if (
        !!activeTooltip &&
        (
            (prevVisible && !visible) ||
            (prevExpanded && !expanded)
        )
    ) {
        setActiveTooltip(null);
    }

    return (
        <BottomBarAnchor>
            <BottomBar
                visible={visible || actionsMenuIsOpen}
                expanded={expanded}
                actionsMenuIsOpen={actionsMenuIsOpen}
                onClick={event => stopPropagation(event)}
            >
                <HamburgerContainer>
                    <Hamburger
                        toggled={expanded}
                        toggle={toggleExpanded}
                        direction="left"
                        color="#ccc"
                        size={18}
                        rounded
                    />
                </HamburgerContainer>

                <ShrinkZone>
                    <Gap20 />

                    {
                        showTitle &&
                        <PresentationName data-testing-info="deck-presentation-name">{presentationName}</PresentationName>
                    }

                    {
                        showTitle &&
                        showAuthor &&
                        <>
                            <Gap20 />
                            <Divider />
                            <Gap20 />
                        </>
                    }

                    {
                        showAuthor &&
                        creator &&
                        <CreatorContainer>
                            {creator.photoURL && <img src={creator.photoURL}></img>}
                            {!creator.photoURL && <PersonIcon />}
                            <span>{creator.displayName}</span>
                        </CreatorContainer>
                    }

                    {
                        (
                            showTitle ||
                            showAuthor
                        ) &&
                        <Spacer />
                    }

                    {
                        canControlRemotely &&
                        <>
                            <Tooltip
                                open={activeTooltip === "control-from-phone"}
                                onOpen={() => setActiveTooltip("control-from-phone")}
                                onClose={() => handleTooltipClose("control-from-phone")}
                                className="hide-800"
                                title="Control from Phone"
                                arrow
                                placement="top"
                            >
                                <ButtonWithLabel
                                    onClick={() => {
                                        registerUserInteraction?.();
                                        onControlRemotely?.();
                                    }}
                                >
                                    <PhoneAndroid style={{ fontSize: 26 }} />
                                </ButtonWithLabel>
                            </Tooltip>
                            <Gap20 className="hide-800" />
                        </>
                    }

                    <FlexBox middle>
                        <PrevSlideButton onClick={() => {
                            registerUserInteraction?.(false);
                            advanceToSlide(-1);
                            trackSlideNavigation({ method: "buttons", deviceType: "desktop" });
                        } } />
                        <SlideIndex>{currentSlideIndex + 1} / {slidesCount}</SlideIndex>
                        <NextSlideButton onClick={() => {
                            registerUserInteraction?.(false);
                            advanceToSlide(1);
                            trackSlideNavigation({ method: "buttons", deviceType: "desktop" });
                        } } />
                    </FlexBox>

                    <Gap20 className="hide-800" />

                    <Tooltip
                        open={activeTooltip === "slide-grid"}
                        onOpen={() => setActiveTooltip("slide-grid")}
                        onClose={() => handleTooltipClose("slide-grid")}
                        className="hide-800"
                        title="Slide Grid"
                        arrow
                        placement="top"
                    >
                        <ThumbnailsButton onClick={() => {
                            registerUserInteraction?.();
                            showThumbnailsPane?.();
                        }} />
                    </Tooltip>

                    {
                        allowFullscreenOption &&
                        <>
                            <Gap20 className="hide-800" />
                            <Tooltip
                                open={activeTooltip === "fullscreen"}
                                onOpen={() => setActiveTooltip("fullscreen")}
                                onClose={() => handleTooltipClose("fullscreen")}
                                className="hide-800"
                                title={inFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                                arrow
                                placement="top"
                            >
                                <ButtonWithLabel onClick={() => {
                                    registerUserInteraction?.();
                                    !isPlayerSettingsPreview && toggleFullscreen?.();
                                }}>
                                    {
                                        inFullscreen
                                            ? <FullscreenExitIcon />
                                            : <FullscreenIcon />
                                    }
                                </ButtonWithLabel>
                            </Tooltip>
                        </>
                    }
                    {
                        isPresenterAvailable &&
                        !isRemoteControlLeader &&
                        <>
                            <Gap20 className="hide-800" />
                            <Tooltip
                                open={activeTooltip === "presenter"}
                                onOpen={() => setActiveTooltip("presenter")}
                                onClose={() => handleTooltipClose("presenter")}
                                className="hide-800"
                                title="Presenter"
                                arrow
                                placement="top"
                            >
                                <ButtonWithLabel onClick={() => {
                                    registerUserInteraction?.();
                                    togglePresenter?.();
                                }}>
                                    <ChromeReaderModeIcon style={{ fontSize: 22, marginBottom: 2 }} />
                                </ButtonWithLabel>
                            </Tooltip>
                        </>
                    }
                    {
                        isRemoteControlLeader &&
                        <>
                            <Gap20 className="hide-800" />
                            <Tooltip
                                open={activeTooltip === "show-notes"}
                                onOpen={() => setActiveTooltip("show-notes")}
                                onClose={() => handleTooltipClose("show-notes")}
                                className="hide-800"
                                title="Show Notes"
                                arrow
                                placement="top"
                            >
                                <ButtonWithLabel onClick={() => {
                                    registerUserInteraction?.();
                                    toggleNotes?.();
                                }}>
                                    <ChromeReaderModeIcon style={{ fontSize: 22, marginBottom: 2 }} />
                                </ButtonWithLabel>
                            </Tooltip>
                        </>
                    }
                    {
                        hasViewerComments &&
                        <>
                            <Gap20 className="hide-800" />
                            <Tooltip
                                open={activeTooltip === "comments"}
                                onOpen={() => setActiveTooltip("comments")}
                                onClose={() => handleTooltipClose("comments")}
                                className="hide-800"
                                title="Comments"
                                arrow
                                placement="top"
                            >
                                <ButtonWithLabel onClick={() => {
                                    registerUserInteraction?.();
                                    toggleComments?.();
                                }}>
                                    <ModeCommentIcon style={{ fontSize: 22, marginTop: 2 }} />
                                </ButtonWithLabel>
                            </Tooltip>
                        </>
                    }

                    <Spacer />
                    <ButtonWithLabel style={{ marginRight: 0 }}
                        ref={moreButtonRef}
                        onClick={() => onToggleActionsMenu(true)}
                    >
                        <label>Options</label>
                        <Gap10 />
                        <ArrowDropDownIcon />
                    </ButtonWithLabel>

                    <PresentationActionsMenu
                        {...props}
                        open={actionsMenuIsOpen}
                        onClose={() => onToggleActionsMenu(false)}
                        anchorEl={moreButtonRef.current}
                    />
                </ShrinkZone>
            </BottomBar>
        </BottomBarAnchor>
    );
}

export default PlayerControls;
